import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../variables.css";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/LoadingSpinner";
import VolunteerDashboard from "../components/dashboard/VolunteerDashboard";
import InternshipDashboard from "../components/dashboard/InternshipDashboard";
import avatarIcon from "../assets/dashboard-avatar.svg";
import chevronDown from "../assets/chevron-down-dark.svg";

const DESIGN_WIDTH = 1728; // Figma canvas width the fixed px values are based on

// The selector names the role a user acts in; the keys are the work log program
// values each dashboard files under.
const DASHBOARD_LABELS = {
    volunteer: "Volunteer",
    internship: "Intern",
    instruction: "Instructor",
};

export default function MyPage() {
    const { user, isLoadingUser } = useAuth();

    const userName = user?.first_name || user?.username || user?.email?.split("@")[0] || "User";
    // The Volunteer role is granted on application approval (volunteers/models.py);
    // Intern and Instructor are assigned by an admin. Admins get every dashboard. Without access
    // the volunteer panels render behind a locked overlay.
    // Role titles are matched exactly — the server does the same
    // (roles/permissions.py: title__in=[...]) and canonical titles are
    // capitalized ("Admin", "Intern", "Instructor", "Volunteer", "Member"). Lowercasing
    // here would let the client show a dashboard the API then 403s on.
    const roles = new Set(user?.role || []);
    const hasRole = (title) => roles.has(title);
    const isAdmin = hasRole("Admin");
    const canViewVolunteer = isAdmin || hasRole("Volunteer");
    const canViewInternship = isAdmin || hasRole("Intern");
    const canViewInstructor = isAdmin || hasRole("Instructor");

    // Options the user may switch between. Someone with no dashboard at all still
    // lands on Volunteer, where the locked overlay explains how to get access.
    const options = [];
    if (canViewVolunteer) options.push("volunteer");
    if (canViewInternship) options.push("internship");
    if (canViewInstructor) options.push("instruction");

    const [selected, setSelected] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const active = selected ?? options[0] ?? "volunteer";

    // Fit-to-screen: scale the 1728px-wide design to the viewport width, then
    // give the container exactly the leftover height (in design px) so the
    // panels — which flex — absorb it and fill the screen without scrolling.
    // The design is wider than typical 16:9 screens, so width drives the scale.
    // Narrow screens (<1280px) fall back to the normal responsive flow instead:
    // scaling there would make the text unreadably small.
    const [fit, setFit] = useState({ zoom: 1, height: null });
    useEffect(() => {
        if (isLoadingUser) return;
        const measure = () => {
            if (window.innerWidth < 1280) {
                setFit({ zoom: 1, height: null });
                return;
            }
            const headerHeight =
                parseFloat(
                    getComputedStyle(document.documentElement).getPropertyValue("--header-height")
                ) || 80;
            const zoom = window.innerWidth / DESIGN_WIDTH;
            setFit({ zoom, height: (window.innerHeight - headerHeight) / zoom });
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [isLoadingUser]);

    if (isLoadingUser) return <LoadingSpinner />;

    return (
        <div
            className="min-h-screen bg-white text-left"
            style={{
                fontFamily: "Arial, sans-serif",
                paddingTop: "var(--header-height, 80px)",
            }}
        >
            <div
                className="flex flex-col"
                style={
                    fit.height
                        ? {
                              zoom: fit.zoom,
                              width: DESIGN_WIDTH,
                              height: fit.height,
                              margin: "0 auto",
                          }
                        : undefined
                }
            >
                {/* Greeting row */}
                <div className="px-6 sm:px-10 lg:px-[103px] pt-8 flex items-end justify-between">
                    <div className="flex items-center gap-[11px]">
                        <Link
                            to="/edit-profile"
                            aria-label="Edit profile"
                            className="shrink-0"
                        >
                            {/* The uploaded photo when there is one, otherwise
                                the dashboard's own placeholder icon. object-cover
                                keeps a non-square photo from stretching inside
                                the circle. */}
                            <img
                                alt=""
                                src={user?.profile_photo || avatarIcon}
                                className="w-[49px] h-[49px] rounded-full object-cover"
                            />
                        </Link>
                        <h1
                            className="text-[32px] sm:text-[40px] lg:text-[48px] leading-none text-[#06080b] tracking-[2.4px] uppercase"
                            style={{ fontFamily: "Koulen, sans-serif" }}
                        >
                            HI! {userName}
                        </h1>
                    </div>
                    {/* Dashboard selector — the label is omitted while the user
                        has no dashboard at all. */}
                    <div className="hidden sm:flex items-center gap-5 relative">
                        {options.length > 0 && (
                            <span className="text-[24px] text-[#495460]">{DASHBOARD_LABELS[active]}</span>
                        )}
                        <button
                            type="button"
                            onClick={() => options.length > 1 && setMenuOpen(!menuOpen)}
                            className={options.length > 1 ? "cursor-pointer" : "cursor-default"}
                            aria-haspopup={options.length > 1 ? "listbox" : undefined}
                            aria-expanded={options.length > 1 ? menuOpen : undefined}
                        >
                            <img
                                alt=""
                                src={chevronDown}
                                className={`w-[18px] h-[14px] ${menuOpen ? "-scale-y-100" : ""}`}
                            />
                        </button>
                        {menuOpen && (
                            <ul
                                role="listbox"
                                className="absolute right-0 top-full mt-3 z-20 bg-[#d2d4d7] rounded-[10px] py-2 min-w-[160px]"
                            >
                                {options.map((option) => (
                                    <li key={option} role="option" aria-selected={option === active}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelected(option);
                                                setMenuOpen(false);
                                            }}
                                            className="w-full text-left px-5 py-2 text-[20px] text-[#495460] hover:bg-[#c3c6ca]"
                                        >
                                            {DASHBOARD_LABELS[option]}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Double divider */}
                <div className="mx-6 sm:mx-10 mt-4">
                    <div className="border-t-2 border-black" />
                    <div className="border-t-2 border-black mt-[11px]" />
                </div>

                {/* mt-0 overrides the global `main { margin-top }` rule in styles.css.
                    The gap under the divider is padding, not margin, so the locked
                    overlay covers it too. */}
                <main className="mt-0 pt-10 px-6 sm:px-10 lg:px-[103px] pb-5 flex-1 min-h-0 relative">
                    {active === "internship" || active === "instruction" ? (
                        // Keyed so switching programs remounts it: the two share one
                        // component, and an unsaved draft must not carry over.
                        <InternshipDashboard key={active} program={active} />
                    ) : (
                        <VolunteerDashboard locked={!canViewVolunteer} />
                    )}
                </main>
            </div>
        </div>
    );
}
